/**
 * Facturación y cobranza: vencimientos, estado real de una factura, antigüedad
 * de la cartera y días de cobro.
 *
 * Esto reemplaza la aproximación del CRM original, que asumía "+30 días" fijos
 * en tres lugares distintos del código (`renderFacturas`, `computeNotificaciones`
 * y `getAllEventos`) y trataba el estado de la factura como un flag que se
 * escribía a mano. Aquí el vencimiento sale de la condición de pago pactada y el
 * estado se **deriva** de los pagos registrados, así que no puede quedar
 * desincronizado.
 */
import { DIAS_POR_CONDICION_PAGO, DOCUMENTOS_VENTA_NEGATIVOS, } from '../dominio/enums.js';
import { TRAMOS_ANTIGUEDAD } from '../dominio/constantes.js';
import { aFecha, diasEntre, hoy, sumarDias } from '../utiles/fecha.js';
import { redondearPesos } from './iva.js';
// ─────────────────────────────────────────────────────────────
// Vencimientos
// ─────────────────────────────────────────────────────────────
/**
 * Fecha de vencimiento a partir de la emisión y la condición pactada.
 * `CONTADO` vence el mismo día de emisión.
 */
export function calcularVencimiento(fechaEmision, condicionPago) {
    return sumarDias(fechaEmision, DIAS_POR_CONDICION_PAGO[condicionPago]);
}
/**
 * Días de atraso a la fecha de referencia.
 * Positivo = vencida hace tantos días. Negativo = todavía no vence.
 */
export function diasDeAtraso(fechaVencimiento, referencia = new Date()) {
    return diasEntre(fechaVencimiento, hoy(referencia));
}
/**
 * Determina la situación real de una factura.
 *
 * Los estados `PAGADA`, `PAGADA_PARCIAL` y `VENCIDA` nunca se escriben a mano:
 * se calculan aquí. `BORRADOR` y `ANULADA` sí son decisiones humanas y se
 * respetan tal cual — una factura anulada no "vence" ni acumula atraso.
 */
export function evaluarFactura(factura, referencia = new Date()) {
    const total = redondearPesos(factura.montoTotal);
    const montoPagado = redondearPesos(factura.pagos.reduce((suma, pago) => suma + Number(pago.monto || 0), 0));
    const saldo = Math.max(0, total - montoPagado);
    const diasAtraso = diasDeAtraso(factura.fechaVencimiento, referencia);
    const fechasPago = factura.pagos
        .map((pago) => aFecha(pago.fecha))
        .filter((fecha) => fecha !== null)
        .sort((a, b) => a.getTime() - b.getTime());
    const fechaUltimoPago = fechasPago.length ? fechasPago[fechasPago.length - 1] : null;
    const base = {
        montoPagado,
        saldo,
        pagadoPct: total > 0 ? Math.min(100, (montoPagado / total) * 100) : 0,
        diasAtraso,
        fechaUltimoPago,
    };
    // Estados que fija una persona y el sistema no debe pisar.
    if (factura.estado === 'ANULADA' || factura.estado === 'BORRADOR') {
        return {
            ...base,
            estado: factura.estado,
            saldo: 0,
            estaVencida: false,
            diasHastaPagoCompleto: null,
        };
    }
    const pagadaCompleta = total > 0 && montoPagado >= total;
    if (pagadaCompleta) {
        return {
            ...base,
            estado: 'PAGADA',
            saldo: 0,
            estaVencida: false,
            diasHastaPagoCompleto: fechaUltimoPago
                ? diasEntre(factura.fechaEmision, fechaUltimoPago)
                : null,
        };
    }
    const vencida = diasAtraso > 0;
    return {
        ...base,
        // Una factura con abono parcial y atrasada se muestra como VENCIDA: es la
        // señal accionable. El abono sigue visible en `montoPagado` y `pagadoPct`.
        estado: vencida ? 'VENCIDA' : montoPagado > 0 ? 'PAGADA_PARCIAL' : factura.estado,
        estaVencida: vencida,
        diasHastaPagoCompleto: null,
    };
}
/** Signo del documento: las notas de crédito restan de la cartera. */
export function signoDocumento(tipo) {
    return DOCUMENTOS_VENTA_NEGATIVOS.includes(tipo) ? -1 : 1;
}
/** Ubica un atraso en su tramo de antigüedad. */
export function tramoDe(diasAtraso) {
    for (const tramo of TRAMOS_ANTIGUEDAD) {
        const bajoTope = tramo.hasta === null || diasAtraso <= tramo.hasta;
        if (diasAtraso >= tramo.desde && bajoTope)
            return tramo.clave;
    }
    return 'D_90_MAS';
}
/**
 * Distribuye el saldo por cobrar en tramos de antigüedad.
 *
 * Es la vista que responde "¿cuánta plata me deben y hace cuánto?". Facturas
 * pagadas, anuladas y en borrador quedan fuera porque no son cartera.
 */
export function calcularAntiguedad(facturas, referencia = new Date()) {
    const acumulado = new Map();
    for (const tramo of TRAMOS_ANTIGUEDAD) {
        acumulado.set(tramo.clave, { monto: 0, cantidad: 0 });
    }
    let totalPorCobrar = 0;
    let totalVencido = 0;
    for (const factura of facturas) {
        const situacion = evaluarFactura(factura, referencia);
        if (situacion.saldo <= 0)
            continue;
        if (factura.estado === 'ANULADA' || factura.estado === 'BORRADOR')
            continue;
        const saldoConSigno = situacion.saldo * signoDocumento(factura.tipoDocumento);
        const clave = tramoDe(situacion.diasAtraso);
        const celda = acumulado.get(clave);
        celda.monto += saldoConSigno;
        celda.cantidad += 1;
        totalPorCobrar += saldoConSigno;
        if (situacion.estaVencida)
            totalVencido += saldoConSigno;
    }
    const tramos = TRAMOS_ANTIGUEDAD.map((tramo) => ({
        clave: tramo.clave,
        etiqueta: tramo.etiqueta,
        monto: redondearPesos(acumulado.get(tramo.clave).monto),
        cantidad: acumulado.get(tramo.clave).cantidad,
    }));
    return {
        tramos,
        totalPorCobrar: redondearPesos(totalPorCobrar),
        totalVencido: redondearPesos(totalVencido),
    };
}
// ─────────────────────────────────────────────────────────────
// DSO — días promedio de cobro
// ─────────────────────────────────────────────────────────────
/**
 * Días promedio que tarda en pagarse una factura, ponderado por monto.
 *
 * Se pondera por monto y no por cantidad a propósito: que una factura de $50.000
 * se pague rápido no compensa que una de $4.000.000 lleve cuatro meses. Solo
 * cuenta facturas efectivamente pagadas — las pendientes distorsionarían el
 * indicador hacia abajo.
 *
 * Devuelve `null` cuando no hay facturas pagadas todavía.
 */
export function calcularDSO(facturas, referencia = new Date()) {
    let sumaPonderada = 0;
    let sumaMontos = 0;
    for (const factura of facturas) {
        const situacion = evaluarFactura(factura, referencia);
        if (situacion.diasHastaPagoCompleto === null)
            continue;
        if (signoDocumento(factura.tipoDocumento) < 0)
            continue;
        sumaPonderada += situacion.diasHastaPagoCompleto * factura.montoTotal;
        sumaMontos += factura.montoTotal;
    }
    return sumaMontos > 0 ? sumaPonderada / sumaMontos : null;
}
/**
 * Compara el DSO real con la condición pactada.
 *
 * Un organismo que pacta 30 días y paga en 55 tiene un "sobreplazo" de 25 días.
 * Ese número, por organismo, es lo que permite proyectar caja con realismo en
 * vez de creerle a la condición de pago del contrato.
 */
export function calcularSobreplazo(facturas, condicionesPorFactura, referencia = new Date()) {
    let suma = 0;
    let cuenta = 0;
    facturas.forEach((factura, indice) => {
        const situacion = evaluarFactura(factura, referencia);
        if (situacion.diasHastaPagoCompleto === null)
            return;
        const condicion = condicionesPorFactura[indice];
        if (!condicion)
            return;
        suma += situacion.diasHastaPagoCompleto - DIAS_POR_CONDICION_PAGO[condicion];
        cuenta += 1;
    });
    return cuenta > 0 ? suma / cuenta : null;
}
/**
 * Genera la plantilla formal y protocolar para cobro al Estado de Chile,
 * citando el folio SII, la orden de compra y los días de mora transcurridos.
 */
export function generarTextoCobroFormal(datos) {
    const folio = datos.folioOFactura || 'S/N';
    const oc = datos.numeroOrdenCompra ? `Nº ${datos.numeroOrdenCompra}` : 'asignada';
    const montoStr = `$${Math.round(datos.montoTotal).toLocaleString('es-CL')} CLP`;
    let fechaStr = '';
    if (datos.fechaEmision instanceof Date) {
        const d = String(datos.fechaEmision.getUTCDate()).padStart(2, '0');
        const m = String(datos.fechaEmision.getUTCMonth() + 1).padStart(2, '0');
        const y = datos.fechaEmision.getUTCFullYear();
        fechaStr = `${d}/${m}/${y}`;
    }
    else if (typeof datos.fechaEmision === 'string') {
        const p = datos.fechaEmision.slice(0, 10).split('-');
        fechaStr = p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : datos.fechaEmision;
    }
    const moraStr = datos.diasAtraso > 0
        ? ` y con ${datos.diasAtraso} día(s) de mora transcurrido(s)`
        : ' próxima a vencer';
    const firma = datos.nombreEmpresa
        ? `Equipo de Cobranzas ${datos.nombreEmpresa}`
        : 'Equipo de Cobranzas LEXICORE';
    return `Estimada Tesorería y Adquisiciones de ${datos.organismoNombre}, junto con saludar, solicitamos por favor estado de pago y programación de transferencia para la Factura Electrónica Folio Nº ${folio}, correspondiente a la Orden de Compra ${oc}, por un monto total de ${montoStr}, emitida con fecha ${fechaStr}${moraStr}. Adjuntamos copia del documento y Acta de Conformidad firmada. Agradecemos su confirmación. Saludos cordiales, ${firma}.`;
}
/**
 * Genera un enlace directo a WhatsApp Web (wa.me) con el mensaje de cobranza prellenado.
 */
export function generarEnlaceWhatsapp(telefono, mensaje) {
    if (!telefono)
        return null;
    const limpio = telefono.replace(/[^\d+]/g, '');
    if (!limpio)
        return null;
    let numFinal = limpio;
    if (!numFinal.startsWith('+')) {
        if (numFinal.length === 9) {
            numFinal = `56${numFinal}`;
        }
    }
    else {
        numFinal = numFinal.replace('+', '');
    }
    return `https://wa.me/${numFinal}?text=${encodeURIComponent(mensaje)}`;
}
//# sourceMappingURL=cobranza.js.map